import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Autocomplete} from "@mui/material";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";

import {isEmpty, getCookie} from "weed-js";

import {INTEGRATIONS, METRC, DUTCHIE} from "../../constants.js";

import {selectOrganization} from "../../redux/models/organizations/Action.js";
import {selectIntegration, selectIntegrationAccounts} from "../../redux/models/integrations/Action.js";
import {
    fetchLeaflogixAccounts, 
    fetchMetrcAccounts, 
    fetchAlpineIQAccounts, 
    setDutchieAccounts, 
    setMetrcAccounts, 
    setAlpineIQAccounts
} from "../../redux/models/integrations/Action.js";

export const IntegrationDropdowns = ({subscriptions}) => {
    const dispatch = useDispatch();

    const loggedInUser = useSelector(state => state.userReducer.loggedInUser.user);
    const profile = useSelector(state => state.userReducer.loggedInUser.profile);
    
    const organizations = useSelector(state => state.userReducer.loggedInUser.organizations || []);
    const organizationUsers = useSelector(state => state.userReducer.loggedInUser.organizationUsers);
    const selectedOrganization = useSelector(state => state.organizationReducer.selectedOrganization);
    const selectedIntegration = useSelector(state => {
        return isEmpty(state.integrationReducer.selectedIntegration) ? INTEGRATIONS.find(i => i == getCookie("integration")) : state.integrationReducer.selectedIntegration;
    });
    const selectedIntegrationAccounts = useSelector(state => state.integrationReducer.accounts.selectedAccounts);
    const integrationAccounts = useSelector(state => state.integrationReducer.accounts);

    if (isEmpty(loggedInUser) || isEmpty(organizations)) {
        return <></>;
    }

    const hasMetrcSubscription = subscriptions?.reduce((acc, sub) => acc + sub.getMaximumMetrcStores(), 0) > 0;
    const hasDutchieSubscription = subscriptions?.reduce((acc, sub) => acc + sub.getMaximumDutchieStores(), 0) > 0;

    if (!isEmpty(subscriptions)) {
        if (!hasMetrcSubscription && selectedIntegration === METRC) {
            dispatch(selectIntegration(null));
            dispatch(selectIntegrationAccounts([]));
        }
        if (!hasDutchieSubscription && selectedIntegration === DUTCHIE) {
            dispatch(selectIntegration(null));
            dispatch(selectIntegrationAccounts([]));
        }
    }

    const organizationOptions = organizations.map((org) => {
        return {"label": org.name, "value": org.id};
    }).sort((a, b) => ((a.label || "").localeCompare((b.label || ""))));

    const integrationOptions = INTEGRATIONS.filter(integration => {
        if (isEmpty(integrationAccounts[integration])) {
            return false;
        }
        if (integration === METRC && hasMetrcSubscription) {
            return !isEmpty(integrationAccounts[integration].filter(account => account.organization_id == selectedOrganization.id));
        }
        if (integration === DUTCHIE && hasDutchieSubscription) {
            return !isEmpty(integrationAccounts[integration].filter(account => account.organization_id == selectedOrganization.id));
        }
        return !isEmpty(integrationAccounts[integration].filter(account => account.organization_id == selectedOrganization.id));
    }).map(integration => {
        return {label: integration, value: integration};
    });

    const integrationAccountOptions = isEmpty(selectedIntegration) ? [] : (integrationAccounts[selectedIntegration] || []).filter(
        integrationAccount => integrationAccount.organization_id == selectedOrganization.id
    ).map(integrationAccount => {
        return {
            label: integrationAccount.display_name,
            value: integrationAccount
        };
    });

    useEffect(() => {
        if (
            !isEmpty(selectedIntegrationAccounts) ||
            isEmpty(integrationAccounts) ||
            isEmpty(integrationAccounts[selectedIntegration])
        ) {
            return;
        }

        if (!isEmpty(selectedIntegration) && !isEmpty(getCookie("integrationAccountIds"))) {
            const integrationAccountIds = JSON.parse(getCookie("integrationAccountIds"));
            const accounts = integrationAccounts[selectedIntegration];
            const selectedAccounts = accounts.filter(account => integrationAccountIds.find(accountId => accountId === account.id));
            dispatch(selectIntegrationAccounts(selectedAccounts));
        }
    }, [
        selectedOrganization && selectedOrganization.id,
        !isEmpty(selectedIntegration) && integrationAccounts[selectedIntegration]
    ]);

    useEffect(() => {
        if (isEmpty(selectedOrganization)) {
            return;
        }
        Promise.all([
            dispatch(fetchLeaflogixAccounts({organization_id: selectedOrganization.id})),
            dispatch(fetchMetrcAccounts({organization_id: selectedOrganization.id})),
            dispatch(fetchAlpineIQAccounts({organization_id: selectedOrganization.id}))
        ]).then((fetchedAccounts) => {
            const [fetchedDutchieAccounts, fetchedMetrcAccounts, fetchedAlpineIQAccounts] = fetchedAccounts;
            dispatch(setAlpineIQAccounts(fetchedAlpineIQAccounts));
            dispatch(setMetrcAccounts(fetchedMetrcAccounts));
            dispatch(setDutchieAccounts(fetchedDutchieAccounts));
        });
    }, [selectedOrganization, loggedInUser, organizationUsers, profile]);

    const organizationChangeHandler = (event, selectedOption, reason, details) => {
        // if (isEmpty(selectedOption)) {
        //     return;
        // }
        if (isEmpty(selectedOrganization) || selectedOption.value !== selectedOrganization.id) {
            dispatch(selectIntegration(null));
            dispatch(selectIntegrationAccounts([]));
        }
        dispatch(selectOrganization(organizations.find(o => o.id === selectedOption.value)));
    };

    const integrationChangeHandler = (event, selectedOption, reason, details) => {
        if (reason == "clear") { // x button clicked
            dispatch(selectIntegration(null));
            dispatch(selectIntegrationAccounts([]));
            return;
        }
        if (isEmpty(selectedOption)) {
            return;
        }
        if (selectedOption.value != selectedIntegration) {
            dispatch(selectIntegrationAccounts([]));
        }
        dispatch(selectIntegration(INTEGRATIONS.find(i => i == selectedOption.value)));
    };

    const integrationAccountChangeHandler = (event, selectedOptions, reason, details) => {
        dispatch(selectIntegrationAccounts(selectedOptions.map(option => option.value)));
    };

    return <Grid container spacing={1}>
        <Grid item xs={12} sm={12} md={4}>
            <Autocomplete
                filterSelectedOptions
                id="select-organizations-toolbar"
                name="select-organizations"
                options={organizationOptions}
                isOptionEqualToValue={(option, value) => {
                    return option.value === value.value;
                }}
                onChange={organizationChangeHandler}
                disablePortal={true}
                disableClearable={true}
                value={selectedOrganization ? {
                    label: selectedOrganization.name,
                    value: selectedOrganization.id
                } : null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Choose an organization"
                        placeholder="Choose an organization"
                    />
                )}
            />
        </Grid>
        <Grid item xs={12} sm={12} md={2}>
            <Autocomplete
                filterSelectedOptions
                id="select-integration-toolbar"
                name="select-integration"
                options={integrationOptions}
                disablePortal={true}
                isOptionEqualToValue={(option, value) => {
                    return option.value === value.value;
                }}
                onChange={integrationChangeHandler}
                value={!isEmpty(selectedIntegration) ? {
                    label: selectedIntegration,
                    value: selectedIntegration
                } : null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Choose an integration"
                        placeholder="Choose an integration"
                    />
                )}
            />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
            <Autocomplete
                filterSelectedOptions
                id="select-integration-account-toolbar"
                name="select-integration-account"
                options={integrationAccountOptions}
                disablePortal={true}
                multiple={true}
                isOptionEqualToValue={(option, value) => {
                    if (isEmpty(option.value) && !isEmpty(value)) {
                        return false;
                    }
                    if (!isEmpty(option.value) && isEmpty(value)) {
                        return false;
                    }
                    return option.value.id === value.value.id;
                }}
                onChange={integrationAccountChangeHandler}
                value={selectedIntegrationAccounts.map(ia => {
                    return {label: ia.display_name, value: ia};
                })}
                renderInput={(params) => {
                    return <TextField
                        {...params}
                        label="Choose an account/location"
                        placeholder="Choose an account/location"
                    />;
                }
                }
            />
        </Grid>
    </Grid>;
};

